/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Profile > Email: connect, view and remove the user's mailbox, and edit the
 * signature. The connect flow and provider list are shared with the inbox
 * through hooks/useEmailProviderConnect.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Modal,
  Snackbar,
  Alert,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from 'auth/Auth';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import moment from 'moment';
import EmailSignatureEditor from 'components/EmailSignatureEditor';
import colors from 'assets/theme/base/colors';
import tokens from 'styles/tokens/colors';
import useEmailProviderConnect, { providerForAccount } from 'hooks/useEmailProviderConnect';

function ProviderCard({ provider, connecting, onConnect }) {
  const busy = connecting === provider.key;
  return (
    <Card
      component="button"
      type="button"
      onClick={() => onConnect(provider.key)}
      disabled={Boolean(connecting)}
      aria-label={`Connect ${provider.label}`}
      sx={{
        width: '100%',
        p: 3,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        textAlign: 'left',
        cursor: connecting ? 'default' : 'pointer',
        border: `1px solid ${tokens.callCenter.cardBorder}`,
        boxShadow: 'none',
        borderRadius: '12px',
        font: 'inherit',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover:not(:disabled)': {
          borderColor: tokens.brand.primary,
          boxShadow: `0 4px 14px ${alpha(tokens.brand.primary, 0.12)}`,
        },
        '&:focus-visible': { outline: `2px solid ${tokens.brand.primary}`, outlineOffset: 2 },
        '&:disabled': { opacity: busy ? 1 : 0.6 },
      }}
    >
      <Box component="img" src={provider.logo} alt="" sx={{ width: 40, height: 40, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <MDTypography variant="h6" sx={{ fontSize: '1rem' }}>
          {busy ? `Waiting for ${provider.label}...` : `Connect ${provider.label}`}
        </MDTypography>
        <MDTypography variant="caption" color="text" component="div">
          {provider.description}
        </MDTypography>
      </Box>
      {busy && <CircularProgress size={20} />}
    </Card>
  );
}

function EmailSettings() {
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const [emailConfig, setEmailConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const notify = useCallback((message, severity = 'success') => setSnackbar({ open: true, message, severity }), []);

  const fetchEmailConfig = useCallback(
    async ({ background = false } = {}) => {
      if (!background) setLoading(true);
      try {
        const response = await axiosInstance.get('/email-settings');
        setEmailConfig(response.data && response.data.email_provider ? response.data : null);
        setLoadError(false);
      } catch (error) {
        console.error('Error fetching email config:', error);
        if (!background) setLoadError(true);
      } finally {
        if (!background) setLoading(false);
      }
    },
    [axiosInstance]
  );

  const connector = useEmailProviderConnect({
    onConnected: () => {
      notify('Email connected successfully');
      fetchEmailConfig({ background: true });
    },
    onFailed: (message) => notify(message || 'Failed to connect email', 'error'),
    onPopupClosed: () => fetchEmailConfig({ background: true }),
  });

  useEffect(() => {
    if (isAuthReady && isAuthenticated) fetchEmailConfig();
  }, [isAuthReady, isAuthenticated, fetchEmailConfig]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleRemoveConfig = async () => {
    setIsRemoving(true);
    try {
      await axiosInstance.delete('/email-settings');
      setOpenDialog(false);
      await fetchEmailConfig();
      notify('Email configuration removed successfully');
    } catch (error) {
      console.error('Error removing email config:', error);
      notify('Failed to remove email configuration', 'error');
    } finally {
      setIsRemoving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  const connectedProvider = providerForAccount(emailConfig?.email_provider);
  const { providers } = connector;

  return (
    <Box>
      <MDTypography variant="h6" mb={3}>
        Email Configuration
      </MDTypography>

      {loadError ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => fetchEmailConfig()}>
              Try again
            </Button>
          }
        >
          Your email settings could not be loaded. Your mailbox connection is unaffected.
        </Alert>
      ) : emailConfig ? (
        <>
          <Card sx={{ p: 3, boxShadow: 'none', border: `1px solid ${tokens.callCenter.cardBorder}`, borderRadius: '12px' }}>
            <MDTypography variant="subtitle1" fontWeight="medium" color="text" mb={2}>
              Connected Email Account
            </MDTypography>
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
              <Box display="flex" alignItems="center" gap={2} minWidth={0}>
                {connectedProvider && (
                  <Box component="img" src={connectedProvider.logo} alt="" sx={{ width: 40, height: 40, flexShrink: 0 }} />
                )}
                <Box minWidth={0}>
                  <MDTypography variant="body1" fontWeight="medium" color="dark">
                    {emailConfig.email_provider}
                  </MDTypography>
                  <MDTypography variant="body2" color="text" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                    {emailConfig.email_address}
                  </MDTypography>
                  {emailConfig.created_at && (
                    <MDTypography variant="caption" color="text" sx={{ display: 'block', mt: 0.5 }}>
                      Connected on {moment(emailConfig.created_at).format('MMMM DD, YYYY')}
                    </MDTypography>
                  )}
                </Box>
              </Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setOpenDialog(true)}
                sx={{ textTransform: 'none', fontWeight: 'medium', color: 'error.main', borderColor: 'error.main' }}
              >
                Remove Connection
              </Button>
            </Box>
          </Card>

          {/* Only show signature editor when email is configured */}
          <EmailSignatureEditor />
        </>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDTypography variant="body2" color="text">
              {providers.length === 0
                ? 'No email providers are available on this workspace yet. Ask your administrator to enable Gmail or Outlook.'
                : 'Connect a mailbox to read and send email from the Pearl inbox.'}
            </MDTypography>
          </Grid>
          {providers.map((provider) => (
            <Grid item xs={12} sm={providers.length === 1 ? 12 : 6} key={provider.key} sx={providers.length === 1 ? { maxWidth: 420 } : undefined}>
              <ProviderCard provider={provider} connecting={connector.connecting} onConnect={connector.connect} />
            </Grid>
          ))}
          {connector.connecting && (
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <MDTypography variant="caption" color="text">
                  Finish signing in in the window that opened.
                </MDTypography>
                <Button size="small" onClick={connector.cancel}>
                  Cancel
                </Button>
              </Box>
            </Grid>
          )}
          {connector.error && !connector.connecting && (
            <Grid item xs={12}>
              <Alert severity="error">{connector.error}</Alert>
            </Grid>
          )}
        </Grid>
      )}

      <Modal
        open={openDialog}
        onClose={() => {
          if (!isRemoving) setOpenDialog(false);
        }}
        aria-labelledby="remove-email-config-title"
        aria-describedby="remove-email-config-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            maxWidth: 'calc(100vw - 32px)',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box className="pearl-popup-header" sx={{ backgroundColor: colors.popupHeader.background, px: 4, py: 3 }}>
            <MDTypography id="remove-email-config-title" variant="h6" component="h2" sx={{ color: '#FFFFFF' }}>
              Remove Email Configuration
            </MDTypography>
          </Box>

          <Box sx={{ p: 4 }}>
            <MDTypography id="remove-email-config-description" variant="body1" mb={3} sx={{ fontSize: '16px', lineHeight: 1.6 }}>
              Are you sure you want to remove this email configuration? This action cannot be undone.
            </MDTypography>

            <Alert severity="info" sx={{ mb: 4 }}>
              Removing this connection will stop inbox syncing and email sending until an account is connected again.
            </Alert>

            <Box display="flex" justifyContent="flex-end" mt={4}>
              <MDButton
                onClick={() => setOpenDialog(false)}
                variant="outlined"
                color="secondary"
                disabled={isRemoving}
                sx={{ marginRight: 2 }}
              >
                Cancel
              </MDButton>
              <MDButton
                onClick={handleRemoveConfig}
                variant="gradient"
                color="error"
                disabled={isRemoving}
                startIcon={isRemoving ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ px: 3, py: 1.5, fontSize: '14px', fontWeight: '600', textTransform: 'none', borderRadius: 2 }}
              >
                {isRemoving ? 'Removing...' : 'Remove'}
              </MDButton>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default EmailSettings;
